import { LitElement, css, html, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators';
import { getFromLocalStorage, setLocalStorage } from '../helpers/utils';

import { logWithAmplitude } from '../helpers/api/api';
import { palette } from '../style/variables';

@customElement('onboarding-call-notification')
export class OnboardingCallNotification extends LitElement {
    static amplitude_callsite: string = 'Onboarding Call Notification';

    @property({ type: Boolean })
    visible: boolean = false;

    @property({ type: String })
    placement: 'backpack' | 'admin' = 'backpack';

    private readonly STORAGE_KEY_DISMISSED = 'onboarding_call_dismissed';
    private readonly STORAGE_KEY_COMPLETED = 'onboarding_call_completed';
    private readonly STORAGE_KEY_LAST_SHOWN = 'onboarding_call_last_shown';
    private readonly DAYS_TO_RESHOW = 3;

    connectedCallback() {
        super.connectedCallback();
        this.checkShouldShow();
    }

    private checkShouldShow() {
        const isCompleted = getFromLocalStorage(this.STORAGE_KEY_COMPLETED) === 'true';
        const isDismissed = getFromLocalStorage(this.STORAGE_KEY_DISMISSED) === 'true';
        const lastShown = getFromLocalStorage(this.STORAGE_KEY_LAST_SHOWN);

        // Don't show if user has completed the call
        if (isCompleted) {
            this.visible = false;
            return;
        }

        // If never shown or dismissed, show immediately
        if (!lastShown) {
            this.visible = true;
            this.markAsShown();
            return;
        }

        // If dismissed, check if enough time has passed to show again
        if (isDismissed) {
            const lastShownDate = new Date(lastShown);
            const now = new Date();
            const daysSinceLastShown = (now.getTime() - lastShownDate.getTime()) / (1000 * 60 * 60 * 24);

            if (daysSinceLastShown >= this.DAYS_TO_RESHOW) {
                this.visible = true;
                this.markAsShown();
                this.clearDismissal();
            } else {
                this.visible = false;
            }
        } else {
            this.visible = true;
        }
    }

    private markAsShown() {
        setLocalStorage(this.STORAGE_KEY_LAST_SHOWN, new Date().toISOString());
    }

    private clearDismissal() {
        setLocalStorage(this.STORAGE_KEY_DISMISSED, 'false');
    }

    private handleBookCall() {
        logWithAmplitude(OnboardingCallNotification.amplitude_callsite, 'Book Call Clicked', {
            placement: this.placement
        });
        
        // Open booking link in new tab
        window.open('#demo?user_type=existing', '_blank');
        
        // Hide notification
        this.visible = false;
    }

    private handleDismiss() {
        logWithAmplitude(OnboardingCallNotification.amplitude_callsite, 'Notification Dismissed', {
            placement: this.placement
        });
        
        setLocalStorage(this.STORAGE_KEY_DISMISSED, 'true');
        this.visible = false;
    }

    private handleCompleted() {
        logWithAmplitude(OnboardingCallNotification.amplitude_callsite, 'Marked as Completed', {
            placement: this.placement
        });
        
        setLocalStorage(this.STORAGE_KEY_COMPLETED, 'true');
        this.visible = false;
    }

    render() {
        if (!this.visible) {
            return html``;
        }

        return html`
            <div class="notification-container">
                <div class="notification-content">
                    <div class="notification-header">
                        <div class="icon">📞</div>
                        <h3>Unlock Proxi's Full Potential</h3>
                    </div>
                    <div class="notification-body">
                        <p>Book a free 1:1 onboarding session</p>
                    </div>
                    <div class="notification-actions">
                        <form-button
                            variant="raised"
                            size="sm"
                            backgroundColor="${palette.primaryBlue}"
                            textColor="${palette.whiteColor}"
                            @click="${this.handleBookCall}">
                            Book Free Session
                        </form-button>
                        <form-button
                            variant="outline"
                            size="sm"
                            @click="${this.handleCompleted}">
                            Mark Completed
                        </form-button>
                    </div>
                    <button class="close-btn" @click="${this.handleDismiss}" title="Dismiss">×</button>
                </div>
            </div>
        `;
    }

    static get styles() {
        return css`
            .notification-container {
                margin-bottom: 1rem;
                border: 1px solid ${unsafeCSS(palette.primaryBlue)};
                border-radius: 8px;
                background: linear-gradient(135deg, ${unsafeCSS(palette.accentSky)} 0%, ${unsafeCSS(palette.whiteColor)} 100%);
                box-shadow: 0 2px 8px rgba(16, 44, 61, 0.1);
                overflow: hidden;
            }

            .notification-content {
                padding: 1rem;
                display: flex;
                flex-direction: row;
                gap: 1rem;
                align-items: center;
            }

            .notification-header {
                display: flex;
                align-items: center;
                gap: 0.5rem;
            }

            .icon {
                font-size: 1.25rem;
                line-height: 1;
            }

            .notification-header h3 {
                flex: 1;
                margin: 0;
                font-size: 0.9rem;
                font-weight: 600;
                color: ${unsafeCSS(palette.primaryBlue)};
                font-family: var(--custom-font), Montserrat, sans-serif;
            }

            .close-btn {
                background: none;
                border: none;
                font-size: 1.25rem;
                color: ${unsafeCSS(palette.lightText)};
                cursor: pointer;
                padding: 0;
                width: 20px;
                height: 20px;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 2px;
                transition: background-color 0.2s ease, color 0.2s ease;
            }

            .close-btn:hover {
                background-color: rgba(16, 44, 61, 0.1);
                color: ${unsafeCSS(palette.primaryBlue)};
            }

            .notification-body p {
                margin: 0;
                font-size: 0.8rem;
                color: ${unsafeCSS(palette.primaryText)};
                line-height: 1.4;
            }

            .notification-actions {
                display: flex;
                gap: 0.5rem;
                flex-direction: row;
            }

            .notification-actions form-button {
                flex: none;
            }

            /* Responsive adjustments */
            @media (max-width: 768px) {
                .notification-container {
                    margin: 0.75rem 0;
                }

                .notification-content {
                    padding: 0.75rem;
                }

                .notification-header h3 {
                    font-size: 0.85rem;
                }

                .notification-body p {
                    font-size: 0.75rem;
                }

                .notification-actions {
                    flex-direction: column;
                }

                .notification-actions form-button {
                    width: 100%;
                }
            }
        `;
    }
}